import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiClientService } from '../../core/http';
import type {
  AutocompleteResult,
  ChangeCostPriceRequest,
  ChangePriceRequest,
  ChangeStatusRequest,
  CorrectIdentityRequest,
  CorrectVariantRequest,
  InventoryListFilters,
  InventoryListResponse,
  InventoryUnitDto,
  PatchInventoryUnitRequest,
  RegisterUnitRequest,
  ReassignStoreRequest,
  StockTreeFilters,
  StockTreeNode
} from './models/inventory.model';

@Injectable({ providedIn: 'root' })
export class InventoryService {
  private readonly api = inject(ApiClientService);

  registerUnit(body: RegisterUnitRequest): Observable<{ data: InventoryUnitDto }> {
    return this.api.post<{ data: InventoryUnitDto }>('/inventory', body);
  }

  patchUnit(id: string, body: PatchInventoryUnitRequest): Observable<{ data: InventoryUnitDto }> {
    return this.api.patch<{ data: InventoryUnitDto }>(`/inventory/${id}`, body);
  }

  deleteUnit(id: string): Observable<{ data: { id: string; deletedAt: string | null } }> {
    return this.api.delete<{ data: { id: string; deletedAt: string | null } }>(`/inventory/${id}`);
  }

  getUnitById(id: string): Observable<{ data: InventoryUnitDto }> {
    return this.api.get<{ data: InventoryUnitDto }>(`/inventory/${id}`);
  }

  listUnits(params?: InventoryListFilters): Observable<InventoryListResponse> {
    const queryParams: Record<string, string | number | boolean | null | undefined> | undefined = params
      ? {
          ...(params.page !== undefined ? { page: params.page } : {}),
          ...(params.limit !== undefined ? { limit: params.limit } : {}),
          ...(params.storeId !== undefined ? { storeId: params.storeId } : {}),
          ...(params.status !== undefined ? { status: params.status } : {}),
          ...(params.variantId !== undefined ? { variantId: params.variantId } : {}),
          ...(params.imei !== undefined ? { imei: params.imei } : {}),
          ...(params.serialNumber !== undefined ? { serialNumber: params.serialNumber } : {}),
          ...(params.brand !== undefined ? { brand: params.brand } : {}),
          ...(params.model !== undefined ? { model: params.model } : {}),
          ...(params.color !== undefined ? { color: params.color } : {}),
          ...(params.capacity !== undefined ? { capacity: params.capacity } : {}),
          ...(params.supplierId !== undefined ? { supplierId: params.supplierId } : {}),
          ...(params.hasBox !== undefined ? { hasBox: params.hasBox } : {}),
          ...(params.isRefurbished !== undefined ? { isRefurbished: params.isRefurbished } : {})
        }
      : undefined;

    return this.api.get<InventoryListResponse>('/inventory', queryParams);
  }

  changeStatus(id: string, body: ChangeStatusRequest): Observable<{ data: InventoryUnitDto }> {
    return this.api.patch<{ data: InventoryUnitDto }>(`/inventory/${id}/status`, body);
  }

  reassignStore(id: string, body: ReassignStoreRequest): Observable<{ data: InventoryUnitDto }> {
    return this.api.patch<{ data: InventoryUnitDto }>(`/inventory/${id}/store`, body);
  }

  changePrice(id: string, body: ChangePriceRequest): Observable<{ data: InventoryUnitDto }> {
    return this.api.patch<{ data: InventoryUnitDto }>(`/inventory/${id}/price`, body);
  }

  changeCostPrice(id: string, body: ChangeCostPriceRequest): Observable<{ data: InventoryUnitDto }> {
    return this.api.patch<{ data: InventoryUnitDto }>(`/inventory/${id}/cost-price`, body);
  }

  correctIdentity(id: string, body: CorrectIdentityRequest): Observable<{ data: InventoryUnitDto }> {
    return this.api.patch<{ data: InventoryUnitDto }>(`/inventory/${id}/identity`, body);
  }

  correctVariant(id: string, body: CorrectVariantRequest): Observable<{ data: InventoryUnitDto }> {
    return this.api.patch<{ data: InventoryUnitDto }>(`/inventory/${id}/variant`, body);
  }

  getStockTree(filters?: StockTreeFilters): Observable<{ data: StockTreeNode[] }> {
    const queryParams: Record<string, string | number | boolean | null | undefined> | undefined = filters
      ? {
          ...(filters.storeId !== undefined ? { storeId: filters.storeId } : {}),
          ...(filters.status !== undefined ? { status: filters.status } : {}),
          ...(filters.brand !== undefined ? { brand: filters.brand } : {}),
          ...(filters.model !== undefined ? { model: filters.model } : {}),
          ...(filters.color !== undefined ? { color: filters.color } : {}),
        ...(filters.capacity !== undefined ? { capacity: filters.capacity } : {}),
        ...(filters.supplierId !== undefined ? { supplierId: filters.supplierId } : {}),
        ...(filters.includeInactive !== undefined ? { includeInactive: filters.includeInactive } : {})
        }
      : undefined;

    return this.api.get<{ data: StockTreeNode[] }>('/stock/tree', queryParams);
  }

  getStockAutocomplete(q: string, storeId?: string): Observable<{ data: AutocompleteResult[] }> {
    return this.api.get<{ data: AutocompleteResult[] }>('/stock/autocomplete', {
      q,
      ...(storeId !== undefined ? { storeId } : {})
    });
  }
}
